import * as React from 'react';
import {useContext, useState} from 'react';
import {useTranslation} from 'react-i18next'
import * as Yup from 'yup';
import {
    ID_FILTER_DEALERS,
    ID_FILTER_LEGAL_ENTITIES,
    ID_FILTER_REPORT_FILE_NAME,
    ID_FILTER_REPORT_IMPORT_DATE_FROM,
    ID_FILTER_REPORT_IMPORT_DATE_TO,
    ID_FILTER_REPORT_IMPORTED_BY,
    ID_FILTER_REPORT_STATUS,
    ID_FILTER_REPORT_SUPPLIER,
    ID_FILTER_REPORT_TOTAL_REFUELLING_FROM,
    ID_FILTER_REPORT_TOTAL_REFUELLING_TO,
} from "../constants/FuelFilters";
import {addFilter, getFilterValue} from "../../../utils/FilterUtils";
import {useRecoilValue, useSetRecoilState} from 'recoil';
import {STATE_FUEL_ACTIVE_FILTERS} from '../constants/FuelCardRecoil';
import AdvancedFilter from "../../../component/AdvancedFilter";
import {Checkbox, ListItemText, MenuItem, TextField} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers";
import {asEndOfDay, asStartOfDay} from "../../../utils/FinanceDateUtils";
import {ENI, ERROR, fcReportFilterdefaultValues, IMPORTED, IP, PROCESSING, Q8} from "../constants/FuelsConstants";
import {useFuelStatuses} from "../constants/useFuelStatuses";
import {GlobalContext} from '../../../../../util/state/GlobalContextProvider';

function FuelCardsReportsFilter() {
    const {t} = useTranslation('finance', {keyPrefix: 'fuel'});
    const { t: tg } = useTranslation("translation");
    const setFilterDefs = useSetRecoilState(STATE_FUEL_ACTIVE_FILTERS);
    const filterDefs = useRecoilValue(STATE_FUEL_ACTIVE_FILTERS);
    const { getReportStatus } = useFuelStatuses();

    // Use Context
    const {
        globalData: { user },
    } = useContext(GlobalContext);

    // Setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: any[] = user.legalEntities
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
        value: x.name,
        label: tg(`legalEntities.${x.name}`),
    }));

    const dealerOptions: any[] = user.dealers
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
        value: x.name,
        label: tg(`dealers.${x.name}`),
    }));
    const [dealers, setDealers] = useState<any[]>(dealerOptions);

    const schema = Yup.object().shape({
        supplier: Yup.array(),
        importDateFrom: Yup.date().nullable().test("testImportDateFrom", t('messages.invalidDate'), (value, context) => {
            return !value || !context.parent.importDateTo || (value <= context.parent.importDateTo);
        }).typeError(t('messages.invalidDate')),
        importDateTo: Yup.date().nullable().test("testImportDateTo", t('messages.invalidDate'), (value, context) => {
            return !value || !context.parent.importDateFrom || (value >= context.parent.importDateFrom);
        }).typeError(t('messages.invalidDate')),
        fileName: Yup.string(),
        importedBy: Yup.string(),
        totalRefuellingFrom: Yup.number().transform((value) => Number.isNaN(value) ? null : value )
            .nullable().min(0, t('messages.totalCheck'))
            .test("totalRefuellingFrom", t('messages.totalCheck'), (value, context) => {
            return (!value || !context.parent.totalRefuellingTo || value <= context.parent.totalRefuellingTo)
        }),
        totalRefuellingTo: Yup.number().transform((value) => Number.isNaN(value) ? null : value )
            .nullable().min(1, t('messages.totalCheck'))
            .test("totalRefuellingTo", t('messages.totalCheck'), (value, context) => {
            return (!value || !context.parent.totalRefuellingFrom || value >= context.parent.totalRefuellingFrom)
        }),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne')),
    });

    const defaultValues = {
        supplier: getFilterValue(ID_FILTER_REPORT_SUPPLIER, filterDefs) || [] as Array<string>,
        status: [] as Array<string>,
        importDateFrom: getFilterValue(ID_FILTER_REPORT_IMPORT_DATE_FROM, filterDefs) || null,
        importDateTo: getFilterValue(ID_FILTER_REPORT_IMPORT_DATE_TO, filterDefs) || null,
        fileName: getFilterValue(ID_FILTER_REPORT_FILE_NAME, filterDefs) || '',
        importedBy: getFilterValue(ID_FILTER_REPORT_IMPORTED_BY, filterDefs) || '',
        totalRefuellingFrom: getFilterValue(ID_FILTER_REPORT_TOTAL_REFUELLING_FROM, filterDefs) || "",
        totalRefuellingTo: getFilterValue(ID_FILTER_REPORT_TOTAL_REFUELLING_TO, filterDefs) || "",
        legalEntities:
                getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) ||
                legalEntityOptions.map((x: any) => x.value),
        dealers: getFilterValue(ID_FILTER_DEALERS, filterDefs) || []
    }


    const renderSupplierField =  (field: any) => (
        <TextField
            style={{width: "100%"}}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => selected.map((s: any) => suppliers.find(ss => ss.value === s)!.label).join(', ')
            }}
            label={t('filters.' + ID_FILTER_REPORT_SUPPLIER)}
            value={field.value}
            onChange={field.onChange}
        >
            {suppliers.map((supplier) => (
                <MenuItem key={supplier.value} value={supplier.value}>
                    <Checkbox checked={field.value.indexOf(supplier.value) > -1}/>
                    <ListItemText primary={supplier.label}/>
                </MenuItem>
            ))}
        </TextField>
    )

    const renderStatusesField = (field: any, error: any, state: any, values: any) => (
        <TextField
            style={{width: "100%"}}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
            }}
            label={t('filters.' + ID_FILTER_REPORT_STATUS)}
            value={field.value}
            onChange={field.onChange}
        >
            {statuses.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                    <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                    <ListItemText primary={status.label}/>
                </MenuItem>
            ))}
        </TextField>
    );

    const renderImportDateFromField = (field: any, error: any, state: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.' + ID_FILTER_REPORT_IMPORT_DATE_FROM)}
            value={field.value}
            maxDate={values['importDateTo'] !== null ? values['importDateTo'] : null}
            onChange={field.onChange}
            renderInput={(params) =>
                <TextField {...params} style={{width: "100%"}}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                           id={"importDateFrom"}
                />}
            inputFormat={"L"}
        />
    )
    const renderImportDateToField = (field: any, error: any, state: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.' + ID_FILTER_REPORT_IMPORT_DATE_TO)}
            value={field.value}
            minDate={values['importDateFrom'] !== null ? values['importDateFrom'] : null}
            onChange={field.onChange}
            renderInput={(params) =>
                <TextField {...params} style={{width: "100%"}}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                           id={"importDateTo"}
                />}
            inputFormat={"L"}
        />
    )

    const renderFileNameField = (field: any) => (
        <TextField
            fullWidth
            value={field.value}
            onChange={field.onChange}
            label={t('filters.fileName')}
        />
    )
    const renderImportedByField = (field: any) => (
        <TextField
            fullWidth
            value={field.value}
            onChange={field.onChange}
            label={t('filters.importedBy')}
        />
    )
    const renderTotalRefuellingFromField = (field: any, error: any) => (
        <TextField
            fullWidth
            type="number"
            value={field.value}
            onChange={field.onChange}
            label={t('filters.totalRefuellingFrom')}
            error={error !== undefined}
            helperText={error ? error.message : ""}
        />
    )
    const renderTotalRefuellingToField = (field: any, error: any) => (
        <TextField
            fullWidth
            type="number"
            value={field.value}
            onChange={field.onChange}
            label={t('filters.totalRefuellingTo')}
            error={error !== undefined}
            helperText={error ? error.message : ""}
        />
    )
    const renderMultiSelect = (label: string, list: any[], name: string, isHidden: boolean, onChange?: any) => {
        return (field: any, error: any, formState: any, watchAll: any, setValue: any) => (
            <TextField
            name={name}
            fullWidth
            select
            hidden={isHidden}
            disabled={list.length==0}
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) =>
                selected
                    .map((s: any) => list.find((ss) => ss.value === s)!.label)
                    .join(", "),
            }}
            label={label}
            value={field.value}
            onChange={(event) => {
                if (onChange) {
                onChange(event.target.value, setValue);
                }
                field.onChange(event);
            }}
            error={error !== undefined}
            helperText={error ? error.message : ""}
            >
            {list.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                <Checkbox checked={field.value?.indexOf(item.value) > -1} />
                <ListItemText primary={item.label} />
                </MenuItem>
            ))}
            </TextField>
        );
    };
    const mainFiels = {
        supplier: renderSupplierField,
        fileName: renderFileNameField,
        status: renderStatusesField
    };

    const fields = {
        importedBy: renderImportedByField,
        importDateFrom: renderImportDateFromField,
        importDateTo: renderImportDateToField,
        totalRefuellingFrom: renderTotalRefuellingFromField,
        totalRefuellingTo: renderTotalRefuellingToField,legalEntities: renderMultiSelect(
            tg("shared.legalEntities"),
            legalEntityOptions,
            "legalEntities",
            legalEntityOptions.length <= 1,
            (value: any, setValue: any) => {
              setValue('dealers', []);
              let dealers = user?.dealers
                ?.filter(
                  (x: any) =>
                    value?.length > 0 &&
                    x.legalEntities.some((e: any) => value.includes(e.name)) &&
                    x.name.toLowerCase() !== "all"
                )
                .map((x: any) => {
                  return {
                    label: tg(`dealers.${x.name}`),
                    value: x.name,
                  };
                });
              setDealers(dealers);
            }
          ),
          dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1),
    };

    const suppliers = [
        { value: ENI, label: t('filters.suppliers.' + ENI.toLowerCase())},
        { value: IP,  label: t('filters.suppliers.' + IP.toLowerCase()) },
        { value: Q8,  label: t('filters.suppliers.' + Q8.toLowerCase()) }
    ];

    const statuses = [
        { value: IMPORTED, label: getReportStatus(IMPORTED) },
        { value: ERROR, label: getReportStatus( ERROR) },
        { value: PROCESSING,   label: getReportStatus( PROCESSING) }
    ];
    const submit = (data: any) => {
        addFilter(setFilterDefs, ID_FILTER_REPORT_SUPPLIER, data.supplier);
        addFilter(setFilterDefs, ID_FILTER_REPORT_STATUS, data.status);
        addFilter(setFilterDefs, ID_FILTER_REPORT_IMPORT_DATE_FROM, asStartOfDay(data.importDateFrom));
        addFilter(setFilterDefs, ID_FILTER_REPORT_IMPORT_DATE_TO, asEndOfDay(data.importDateTo));
        addFilter(setFilterDefs, ID_FILTER_REPORT_FILE_NAME, data.fileName);
        addFilter(setFilterDefs, ID_FILTER_REPORT_IMPORTED_BY, data.importedBy);
        addFilter(setFilterDefs, ID_FILTER_REPORT_TOTAL_REFUELLING_FROM, data.totalRefuellingFrom);
        addFilter(setFilterDefs, ID_FILTER_REPORT_TOTAL_REFUELLING_TO, data.totalRefuellingTo);
        addFilter(setFilterDefs, ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilterDefs, ID_FILTER_DEALERS, data.dealers);
    }

    const reset = () => {
        setFilterDefs([]);
        setDealers(dealerOptions);
    }

    return (
        <AdvancedFilter schema={schema}
                        defaultValues={defaultValues}
                        mainFields={mainFiels}
                        fields={fields}
                        onSubmit={submit}
                        onReset={reset}
                        customDefaultValues={fcReportFilterdefaultValues}
        />
    )
}

export default FuelCardsReportsFilter;